package com.videoanalyzer.web.config

import kotlinx.browser.window

/**
 * Frontend configuration with automatic environment detection.
 *
 * Detects environment based on hostname:
 * - localhost / 127.0.0.1 = Development (local backend, production Supabase)
 * - *.vercel.app (not videoscore.io) = Staging (Railway staging, Supabase develop branch)
 * - videoscore.io = Production
 */
object Config {

    // ============================================================
    // Environment Detection
    // ============================================================

    enum class Environment {
        DEVELOPMENT,
        STAGING,
        PRODUCTION
    }

    val CURRENT_ENV: Environment by lazy {
        val hostname = window.location.hostname
        when {
            hostname == "localhost" || hostname == "127.0.0.1" -> Environment.DEVELOPMENT
            hostname.contains("vercel.app") -> Environment.STAGING
            else -> Environment.PRODUCTION
        }
    }

    val IS_PRODUCTION: Boolean by lazy { CURRENT_ENV == Environment.PRODUCTION }
    val IS_STAGING: Boolean by lazy { CURRENT_ENV == Environment.STAGING }
    val IS_DEVELOPMENT: Boolean by lazy { CURRENT_ENV == Environment.DEVELOPMENT }

    // ============================================================
    // Backend API
    // ============================================================

    private const val API_BASE_URL_DEV = "http://localhost:8080"
    private const val API_BASE_URL_STAGING = "https://aivideoanalyzerweb-staging.up.railway.app"
    private const val API_BASE_URL_PROD = "https://api.videoscore.io"

    val API_BASE_URL: String by lazy {
        when (CURRENT_ENV) {
            Environment.DEVELOPMENT -> API_BASE_URL_DEV
            Environment.STAGING -> API_BASE_URL_STAGING
            Environment.PRODUCTION -> API_BASE_URL_PROD
        }
    }

    // ============================================================
    // Supabase Configuration
    // ============================================================

    // Production Supabase
    private const val SUPABASE_URL_PROD = "https://quyhjbpttktwiszngdzr.supabase.co"
    private const val SUPABASE_ANON_KEY_PROD = "sb_publishable_6gijbx_UEQQPS3WRMd6mUQ_jwcgcor1"

    // Staging Supabase (develop branch)
    private const val SUPABASE_URL_STAGING = "https://gbhzxsiwzmsjydlxenbi.supabase.co"
    private const val SUPABASE_ANON_KEY_STAGING = "sb_publishable_HDPwfnYybFLwtJX8LvUpBQ_PaVLkTQ_"

    val SUPABASE_URL: String by lazy {
        when (CURRENT_ENV) {
            Environment.DEVELOPMENT -> SUPABASE_URL_PROD  // Dev uses prod Supabase for now
            Environment.STAGING -> SUPABASE_URL_STAGING
            Environment.PRODUCTION -> SUPABASE_URL_PROD
        }
    }

    val SUPABASE_ANON_KEY: String by lazy {
        when (CURRENT_ENV) {
            Environment.DEVELOPMENT -> SUPABASE_ANON_KEY_PROD
            Environment.STAGING -> SUPABASE_ANON_KEY_STAGING
            Environment.PRODUCTION -> SUPABASE_ANON_KEY_PROD
        }
    }

    // ============================================================
    // Pricing
    // ============================================================

    const val STARTING_PRICE = 7  // Lowest price (10 credits pack)
    const val CURRENCY_SYMBOL = "\u20AC"  // € symbol

    // ============================================================
    // Feature Flags (ConfigCat)
    // ============================================================

    /**
     * ConfigCat SDK keys for feature flags.
     * Get your SDK key from: https://app.configcat.com → Your Config → SDK Key
     *
     * Two environments: Production and Staging (dev uses staging)
     */
    private const val CONFIGCAT_SDK_KEY_PROD = "configcat-sdk-1/rn3eCElguEmFHiorIWZRkA/uqCf0GaENUKoRnylntojqA"
    private const val CONFIGCAT_SDK_KEY_STAGING = "configcat-sdk-1/rn3eCElguEmFHiorIWZRkA/hH9kapSaeEuxjYVMoqVi3A"

    val CONFIGCAT_SDK_KEY: String by lazy {
        when (CURRENT_ENV) {
            Environment.PRODUCTION -> CONFIGCAT_SDK_KEY_PROD
            else -> CONFIGCAT_SDK_KEY_STAGING  // Dev and Staging use same key
        }
    }

    /**
     * Debug mode - automatically disabled in production.
     */
    val DEBUG_MODE: Boolean by lazy { !IS_PRODUCTION }

    /**
     * Show mock data instead of making real API calls.
     */
    const val USE_MOCK_DATA = false
}
